import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  Component,
  computed,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTagsModule } from '@vendasta/galaxy/tags';
import { debounceTime, distinctUntilChanged, filter, Subscription, switchMap } from 'rxjs';
import { SEARCH_DEBOUNCE_MS } from '../constants';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterValueInterface,
  InnerGalaxyFilterChipInjectionToken,
} from '../interface';

const MAX_TAG_LENGTH = 100;

@Component({
  selector: 'glxy-tags-filter-input',
  template: `
    <glxy-form-field bottomSpacing="none">
      <glxy-tags-input
        [placeholder]="'GALAXY.FILTER.CHIP.SELECT_EXPRESSION_PLACEHOLDER' | translate"
        [formControl]="formControl"
        (searchText)="searchTextChanged($event)"
        [maxlength]="MAX_TAG_LENGTH"
        [availableTags]="availableTags() ?? []"
        [uniqueTags]="true"
        [autocompleteOnly]="true"
        [maxTags]="maxTags"
      />
      <glxy-form-field />
    </glxy-form-field>
  `,
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => GalaxyTagsFilterInputComponent) }],
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, GalaxyTagsModule, GalaxyFormFieldModule],
})
export class GalaxyTagsFilterInputComponent implements GalaxyAbstractFilterInput, OnDestroy {
  MAX_TAG_LENGTH = MAX_TAG_LENGTH;

  _tagsFieldId = signal<string>('');
  searchTerm = signal<string>('');
  availableTags = signal<string[]>([]);

  @Input() set tagsFieldId(value: string) {
    this._tagsFieldId.set(value);
  }

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    const tags: string[] = value.map((val) => val?.string || '').filter((tag) => tag.length > 0);
    this.formControl.setValue(tags);
  }

  @Input() maxTags: number | null = null;
  @Input({ transform: booleanAttribute }) set disabled(value: boolean) {
    if (value) {
      this.formControl.disable();
    }
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<string[]> = new FormControl<string[]>([], { nonNullable: true });

  subscriptions: Subscription[] = [];

  private readonly filtersDependencies = inject(InnerGalaxyFilterChipInjectionToken);
  constructor() {
    const tagsUsed = toSignal(this.formControl.valueChanges, { initialValue: [] });
    effect(() => {
      const filterValues = tagsUsed().map<GalaxyFilterValueInterface>((tag) => ({ string: tag }));
      this.valueChanges.emit(filterValues);
    });

    const options$ = toObservable(
      computed(() => {
        return {
          searchTerm: this.searchTerm(),
          tagsFieldId: this._tagsFieldId(),
        };
      }),
    );

    const availableTags$ = options$.pipe(
      filter((options) => options.tagsFieldId.length > 0),
      distinctUntilChanged(),
      debounceTime(SEARCH_DEBOUNCE_MS),
      switchMap((o) => this.filtersDependencies.getFieldOptions(o.tagsFieldId, o.searchTerm)),
    );

    this.subscriptions.push(availableTags$.subscribe((availableTags) => this.availableTags.set(availableTags)));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  searchTextChanged(searchTerm: string) {
    this.searchTerm.set(searchTerm);
  }

  clearValue() {
    this.formControl.setValue([]);
  }
}
